import './polyfills';

import { enableProdMode, importProvidersFrom, provideZoneChangeDetection } from '@angular/core';
import { isProdMode, versionInfo, amBaseUrl } from './config';
import { apiProviders } from './api';
import { RoutingGuardService, serviceProviders } from './services';
import { HTTP_INTERCEPTORS, HttpClient } from '@angular/common/http';
import { ServerErrorInterceptor } from './interceptors/server-error-interceptor';
import { ServerErrorGuard } from './guards/server-error.guard';
import {
  UserPreferencesService,
  StorageService,
  AppGuard,
  MultipleTranslatePoHttpLoader,
  UserRightsModule,
  ShellV2Module,
  FormsRootModule,
  ModalModule,
  SideDrawerModule,
  SpinnerModule,
  PagesV2Module,
  FileUploadModule,
  AssetsModule,
  UnitsModule,
  ShellV2Component,
  MachineSerialsV2Service,
} from '@schenck/rotec-components';
import { provideAnimations } from '@angular/platform-browser/animations';
import { withHashLocation, provideRouter, Routes, Route } from '@angular/router';
import { PagesComponent } from './pages/pages.component';
import { routes } from './routes';
import { TranslateLoader, provideTranslateService } from '@ngx-translate/core';
import { environment } from './environment/environment';
import { bootstrapApplication } from '@angular/platform-browser';

import { DatePipe, registerLocaleData } from '@angular/common';
import deLocale from '@angular/common/locales/de';
import enLocale from '@angular/common/locales/en';
import esLocale from '@angular/common/locales/es';
import frLocale from '@angular/common/locales/fr';
import itLocale from '@angular/common/locales/it';
import nlLocale from '@angular/common/locales/nl';
import plLocale from '@angular/common/locales/pl';
import svLocale from '@angular/common/locales/sv';
import trLocale from '@angular/common/locales/tr';
import zhLocale from '@angular/common/locales/zh';
import huLocale from '@angular/common/locales/hu';
import csLocale from '@angular/common/locales/cs';
import arLocale from '@angular/common/locales/ar';
import koLocale from '@angular/common/locales/ko';
import jaLocale from '@angular/common/locales/ja';
import ptLocale from '@angular/common/locales/pt';
import { provideNativeDateAdapter } from '@angular/material/core';
import { CustomDateAdapter } from './components/datetime-picker/datetime-picker.component';
import { TimePipe } from './components/time.pipe';

registerLocaleData(deLocale, 'de');
registerLocaleData(enLocale, 'en');
registerLocaleData(esLocale, 'es');
registerLocaleData(frLocale, 'fr');
registerLocaleData(itLocale, 'it');
registerLocaleData(nlLocale, 'nl');
registerLocaleData(plLocale, 'pl');
registerLocaleData(svLocale, 'sv');
registerLocaleData(trLocale, 'tr');
registerLocaleData(zhLocale, 'zh');
registerLocaleData(huLocale, 'hu');
registerLocaleData(csLocale, 'cs');
registerLocaleData(arLocale, 'ar');
registerLocaleData(koLocale, 'ko');
registerLocaleData(jaLocale, 'ja');
registerLocaleData(ptLocale, 'pt');

function HttpPoLoaderFactory(http: HttpClient) {
  return new MultipleTranslatePoHttpLoader(http, [
    { prefix: 'assets/rotec-locales/', suffix: '.po' },
    { prefix: 'assets/locales/', suffix: '.po' },
  ]);
}

const conditionRoutes: Route[] = [
  {
    path: '',
    loadComponent: () =>
      import('./pages/condition/condition/condition.component').then((m) => m.ConditionComponent),
    canActivate: [RoutingGuardService],
  },
];

const productionRoutes: Route[] = [
  {
    path: '',
    loadComponent: () =>
      import('./pages/production/production/production.component').then(
        (m) => m.ProductionComponent
      ),
    canActivate: [RoutingGuardService],
  },
];

const statisticsRoutes: Route[] = [
  {
    path: '',
    loadComponent: () =>
      import('./pages/statistics/statistics/statistics.component').then(
        (m) => m.StatisticsComponent
      ),
    canActivate: [RoutingGuardService],
  },
];

const operatorsRoutes: Route[] = [
  {
    path: '',
    loadComponent: () =>
      import('./pages/operators/operators/operators.component').then((m) => m.OperatorsComponent),
    canActivate: [RoutingGuardService],
  },
];

const qualityRoutes: Route[] = [
  {
    path: '',
    loadComponent: () =>
      import('./pages/quality/quality/quality.component').then((m) => m.QualityComponent),
    canActivate: [RoutingGuardService],
  },
];

const notificationsRoutes: Route[] = [
  {
    path: '',
    loadComponent: () =>
      import('./pages/notifications/notifications/notifications.component').then(
        (m) => m.NotificationsComponent
      ),
    canActivate: [RoutingGuardService],
  },
];

const appRoutes: Routes = [
  {
    path: '',
    component: PagesComponent,
    canActivate: [AppGuard],
    children: [
      { path: routes.statistics.url, children: statisticsRoutes },
      { path: routes.production.url, children: productionRoutes },
      { path: routes.quality.url, children: qualityRoutes },
      { path: routes.operators.url, children: operatorsRoutes },
      { path: routes.notifications.url, children: notificationsRoutes },
      { path: routes.condition.url, children: conditionRoutes },
      { path: '**', redirectTo: routes.statistics.url },
    ],
  },
  { path: '**', redirectTo: routes.statistics.url },
];

if (isProdMode) {
  enableProdMode();
}

bootstrapApplication(ShellV2Component, {
  providers: [
    provideZoneChangeDetection(),
    provideTranslateService({
      loader: {
        provide: TranslateLoader,
        useFactory: HttpPoLoaderFactory,
        deps: [HttpClient],
      },
    }),
    importProvidersFrom(
      // Rotec Components Library
      UserRightsModule.forRoot(environment.umBaseUrl),
      ShellV2Module.forRoot({
        isProduction: isProdMode,
        appLogoAndName: {
          name: 'Machine Center',
          logoUrl: './assets/img/machine-center.png',
        },
        versionInfo,
        msalConfig: environment.msalConfig,
        azureEndpoint: environment.azureEndpoint,
        baseUrl: environment.apiBaseUrl,
        usBaseUrl: environment.usBaseUrl,
      }),
      FormsRootModule,
      ModalModule.forRoot(),
      SideDrawerModule.forRoot(),
      SpinnerModule.forRoot(),
      PagesV2Module,
      FileUploadModule.forRoot({
        uploadBaseUrl: amBaseUrl,
        fallbackImgSrcForPipe: './assets/img/myschenckone-hoch.png',
        fallbackImgSrcForFileUpload: './assets/img/favicon.ico',
      }),
      AssetsModule.forRoot(amBaseUrl),
      UnitsModule.forRoot()
    ),
    { provide: provideNativeDateAdapter(), useExisting: CustomDateAdapter },
    CustomDateAdapter,
    TimePipe,
    DatePipe,
    ...apiProviders,
    ...serviceProviders,
    { provide: HTTP_INTERCEPTORS, useClass: ServerErrorInterceptor, multi: true },
    ServerErrorGuard,
    UserPreferencesService,
    StorageService,
    provideAnimations(),
    provideRouter(appRoutes, withHashLocation()),
    MachineSerialsV2Service,
  ],
}).catch((err) => console.log(err));
